import { styled } from "@linaria/react";
import React from "react";
import type { ActiveAnalysis } from "../../../../../api/useActiveAnalysesStream";
import type { IndexedFailureValues } from "../../../../../api/useIndexedFailureValues";
import type { Project } from "../../../../../api/useProjects";
import type { Setup } from "../../../../../api/useSetups";
import HighchartsComponent from "@ui/Highcharts";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import useSeries from "../useSeries";
import useOptions from "./useOptions";
import type { IndexedManualFailureValues } from "../../../../../api/useManualFailureValues";
import type { IndexedFailureMetadata } from "../../../../../api/useFailureMetadata";
import { useFlagSelectedQuantities } from "../hook/useCommonOptions";
import type { Quantity } from "../../../../../api/useQuantities";

const Wrapper = styled.div`
  display: flex;
  width: 250px;
  height: 100%;
`;

const StyledChart = styled(HighchartsComponent)`
  & .highcharts-legend-item .highcharts-point {
    display: none;
  }
`;

interface P {
  visibleSymptomHistogramChartId: string;
  chartRef: React.RefObject<Highcharts.Chart>;
  project: Project;
  setups: Setup[];
  analysis: ActiveAnalysis;
  indexedFailureMetadata: IndexedFailureMetadata;
  indexedFailureValues: IndexedFailureValues;
  indexedManualFailureValues: IndexedManualFailureValues;
  qoi: Quantity;
}

const QuantityChart = ({
  visibleSymptomHistogramChartId,
  chartRef,
  project,
  setups,
  analysis,
  indexedFailureMetadata,
  indexedFailureValues,
  indexedManualFailureValues,
  qoi,
}: P) => {
  const [flagSelectedQuantities] = useFlagSelectedQuantities({
    scope: project.id,
  });
  const quantities = React.useMemo(() => [qoi], [qoi]);

  const setup = getAnalysisSetup(setups, analysis);

  const series = useSeries(
    project,
    analysis,
    indexedFailureMetadata,
    indexedFailureValues,
    indexedManualFailureValues,
    setup,
    quantities,
    false,
  );
  const options = useOptions({
    visibleSymptomHistogramChartId,
    series,
    qoi,
    analysis,
    setups,
    project,
    flagSelectedQuantities,
  });

  return (
    <Wrapper>
      <StyledChart
        name={`Diagnosis QOI ${qoi.name}`}
        ref={chartRef}
        options={options}
        selectablePoints={false}
      />
    </Wrapper>
  );
};

export default React.memo(QuantityChart);
